import { usePersonsQuery } from '@/contact/person/list/list.generated';
import { getPersonDescription } from '@/contact/person/utils';
import { durationSchema } from '@/duration/duration.schema';
import { Frame } from '@/frame';
import {
  CourseModuleStatus,
  CourseUnit,
  CourseUnitStatus,
  SaveCourseUnitInput,
} from '@/graphql/generated/types';
import globalStyles from '@/styles/global.scss';
import { useAdminTagsQuery } from '@/tag/list/list.generated';
import { TagInspect } from '@/tag/tag-inspect/tag-inspect.component';
import { Button } from '@/ui/button/button.component';
import { Chip } from '@/ui/chip/chip.component';
import { Form } from '@/ui/form/form.component';
import { Formatted } from '@/ui/formatted/formatted.component';
import { Grid } from '@/ui/grid/grid.component';
import { useConfirm } from '@/ui/modal/modal.hooks';
import { Section } from '@/ui/section/section.component';
import { Row } from '@/ui/table/row.component';
import { Table } from '@/ui/table/table.component';
import { error, success } from '@/ui/toaster/toast';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, RouteComponentProps, useNavigate } from '@reach/router';
import omitDeep from 'omit-deep-lodash';
import React, { ComponentType, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDuplicateCourseUnit } from 'shared/course/duplicator/course-unit-duplicator.hook';
import { getCourseUnitDates } from 'shared/course/utils';
import {
  PropertyAware,
  PropertySchemaRenderer,
} from 'shared/property-schema/property-schema.component';
import { useYupPropertySchema } from 'shared/property-schema/property-schema.hooks';
import { useResources } from 'shared/resources/resources.provider';
import { array, bool, number, object, string } from 'yup';
import { useCreateCourseModuleMutation } from '../module-editor/editor.generated';
import { useDeleteCourseUnitMutation } from '../overview/course-unit-list/course-unit-list.generated';
import {
  useCourseUnitQuery,
  useUpdateDetailedCourseUnitMutation,
} from './editor.generated';

const schema = object({
  title: string().nullable(),
  subTitle: string().nullable(),
  status: string().required(),
  seasonId: string().nullable(),
  lateBookingOffset: durationSchema,
  description: string().nullable(),
  startDate: string().required(),
  endDate: string().required(),
  withTime: bool(),
  dateUncertain: bool(),
  amount: number().nullable(),
  discountedAmount: number().nullable(),
  earlyBirdDate: string().nullable(),
  earlyBirdNotice: string().nullable(),
  online: bool(),
  correspondenceCourse: bool(),
  modulePriceMatrix: string().nullable(),
  bookingNotes: string().nullable(),
  tagIds: array().of(string()),
});

export const Editor: ComponentType<RouteComponentProps<{ id: string }>> = (
  props,
) => {
  const { id } = props;

  const navigate = useNavigate();

  const { availableLocations, availableSeasons } = useResources();

  const tagsQuery = useAdminTagsQuery({
    variables: { pagination: { take: 999, skip: 0 } },
  });

  const { open: openDuplicationModal, modal: duplicationModal } =
    useDuplicateCourseUnit(() => {});

  const courseUnitQuery = useCourseUnitQuery({
    variables: { id },
    skip: id === undefined,
    fetchPolicy: 'network-only',
  });

  const allTeachers = usePersonsQuery({
    variables: {
      filter: { userType: 'Teacher' },
      pagination: { take: 1000, skip: 0 },
    },
  }).data?.persons.data;

  const [update] = useUpdateDetailedCourseUnitMutation();
  const [remove] = useDeleteCourseUnitMutation();
  const [createModule] = useCreateCourseModuleMutation();

  const properties = useYupPropertySchema(
    courseUnitQuery.data?.adminCourseUnit.course.category.enterprise.id,
    'course-unit',
  );

  const form = useForm<SaveCourseUnitInput & PropertyAware, CourseUnit>({
    resolver: yupResolver(schema.concat(properties)),
    shouldUnregister: true,
    defaultValues: {
      status: CourseUnitStatus.Active,
      online: false,
      dateUncertain: false,
      withTime: false,
      correspondenceCourse: false,
      tagIds: [],
    },
  });

  const courseLocations = useMemo(() => {
    if (courseUnitQuery.data) {
      return availableLocations.filter((location) =>
        location.enterprises
          .map((l) => l.id)
          .includes(
            courseUnitQuery.data?.adminCourseUnit.course.category.enterprise.id,
          ),
      );
    }
    return [];
  }, [courseUnitQuery.data, availableLocations]);

  const secondaryLocations = useMemo(() => {
    return courseLocations.filter((location) => location.parentId === null);
  }, [courseLocations]);

  const courseTeachers = useMemo(() => {
    if (courseUnitQuery.data && allTeachers) {
      return allTeachers.filter((person) =>
        person.enterprises
          .map((l) => l.id)
          .includes(
            courseUnitQuery.data?.adminCourseUnit.course.category.enterprise.id,
          ),
      );
    }
    return [];
  }, [courseUnitQuery.data, allTeachers]);

  const [confirmModal, confirmDelete] = useConfirm(async (id: string) => {
    try {
      await remove({ variables: { id } });
      navigate('/course-managements/units');
      success('Kurs-Einheit gelöscht.');
    } catch (e) {
      error(
        'Fehler',
        'Die Kurs-Einheit kann nicht gelöscht werden, da sie Module oder Events enthält.',
      );
    }
  });

  const onDuplicateCourseModule = useCallback(
    async (moduleIdToDuplicate: string) => {
      if (courseUnitQuery.data?.adminCourseUnit) {
        await createModule({
          variables: {
            moduleIdToDuplicate,
          },
        });
        success('Das Kurs-Modul wurde dupliziert.');
        courseUnitQuery.refetch();
      }
    },
    [courseUnitQuery.data?.adminCourseUnit],
  );

  const onCreateCourseModule = useCallback(async () => {
    if (courseUnitQuery.data?.adminCourseUnit) {
      await createModule({
        variables: {
          input: {
            status: CourseModuleStatus.Active,
            courseUnitId: courseUnitQuery.data.adminCourseUnit.id,
            startDate: courseUnitQuery.data.adminCourseUnit.startDate,
            endDate: courseUnitQuery.data.adminCourseUnit.endDate,
            locationId: courseUnitQuery.data.adminCourseUnit.locationId,
            secondaryLocationId:
              courseUnitQuery.data.adminCourseUnit.secondaryLocationId,
          },
        },
      });
      success('Das Kurs-Modul wurde erstellt.');
      courseUnitQuery.refetch();
    }
  }, [courseUnitQuery.data?.adminCourseUnit]);

  const onSubmit = useMemo(() => {
    return form.handleSubmit(async (input) => {
      try {
        if (id) {
          await update({
            variables: {
              id,
              input: {
                ...(omitDeep(input, '__typename') as any),
                courseId: courseUnitQuery.data?.adminCourseUnit.courseId,
              },
            },
          });

          success('Die Kurs-Einheit wurde gespeichert.');
        }
      } catch (e: any) {
        error('Fehler', e.message);
      }
    });
  }, [id, form.handleSubmit, courseUnitQuery.data]);

  const onDelete = useCallback(async () => {
    if (id) {
      confirmDelete('Soll diese Kurs-Einheit wirklich gelöscht werden?', id);
    }
  }, [id]);

  const withTime = form.watch('withTime');

  return (
    <>
      {confirmModal}
      {duplicationModal}
      <Frame.SubTitle>
        Kurs-Einheit:{' '}
        {courseUnitQuery.data?.adminCourseUnit.title ||
          courseUnitQuery.data?.adminCourseUnit.course.title}
        {courseUnitQuery.data &&
          courseUnitQuery.data.adminCourseUnit.status ===
            CourseUnitStatus.Inactive && (
            <>
              {' '}
              <span className={globalStyles.error}>[INAKTIV]</span>
            </>
          )}
      </Frame.SubTitle>

      <Frame.ActionBar
        left={
          <>
            {courseUnitQuery.data?.adminCourseUnit && (
              <>
                <h3>{courseUnitQuery.data.adminCourseUnit.course?.title}</h3>
                <Button
                  linkTo={`/course-management/courses/${courseUnitQuery.data.adminCourseUnit.courseId}/edit`}
                  label="zum Kurs"
                />
              </>
            )}
          </>
        }
      >
        {id && (
          <>
            <Button
              warning
              transparent
              label="Einheit löschen"
              onClick={onDelete}
            />
            <Button
              label="Duplizieren"
              onClick={() =>
                openDuplicationModal(
                  courseUnitQuery.data?.adminCourseUnit as CourseUnit,
                )
              }
            />
          </>
        )}
        <Button label="Zurück" onClick={() => navigate('..')} />
        <Button primary label="Speichern" onClick={onSubmit} />
      </Frame.ActionBar>
      <Frame.Content>
        <Form form={form} values={courseUnitQuery.data?.adminCourseUnit}>
          <Grid.Row>
            <Grid.Column>
              <Section title="Kurs-Einheit-Eigenschaften">
                <Form.Input
                  name="title"
                  label="Titel (optional, überschreibt den Titel des Kurses)"
                />
                <Form.Input
                  name="subTitle"
                  label="Untertitel (optional, überschreibt den Untertitel des Kurses)"
                />
                <Form.Select
                  label="Status"
                  name="status"
                  choices={[
                    { label: 'Aktiv', value: CourseUnitStatus.Active },
                    { label: 'Inaktiv', value: CourseUnitStatus.Inactive },
                  ]}
                />
                <Form.Select
                  label="Saison"
                  name="seasonId"
                  choices={availableSeasons.map((season) => ({
                    label: season.title || '',
                    value: season.id,
                  }))}
                  nullable
                />
                <Form.Select2
                  name="tagIds"
                  multiple
                  label="Tags"
                  choices={
                    tagsQuery.data?.adminTags.data.map((t) => ({
                      value: t.id,
                      label: t.name,
                    })) || []
                  }
                />
                <Form.Date withTime={withTime} name="startDate" label="Start" />
                <Form.Date withTime={withTime} name="endDate" label="Ende" />
                <Form.Checkbox
                  name="withTime"
                  label="Uhrzeiten berücksichtigen"
                />

                <Form.Checkbox
                  name="dateUncertain"
                  label="Datum noch nicht sicher"
                />
                <Form.Duration
                  name="lateBookingOffset"
                  label="Buchen möglich bis (nach Start)"
                />
                <Form.Select
                  nullable
                  label="Standort"
                  name="locationId"
                  choices={courseLocations.map((location) => ({
                    label: location.name || location.city || '',
                    value: location.id,
                  }))}
                />
                <Form.Select
                  nullable
                  label="Sekundärer Standort"
                  name="secondaryLocationId"
                  choices={secondaryLocations.map((location) => ({
                    label: location.name || location.city || '',
                    value: location.id,
                  }))}
                />
                <Form.Select
                  nullable
                  label="Dozent (primär)"
                  name="teacherId"
                  choices={courseTeachers?.map((person) => ({
                    label:
                      getPersonDescription(person) +
                      ' (' +
                      person.enterprises
                        .map((enterprise) => enterprise.name)
                        .join(', ') +
                      ')',
                    value: person.user?.id,
                  }))}
                />
                <Form.Select2
                  nullable
                  multiple
                  label="(weitere) Dozenten"
                  name="teacherIds"
                  choices={courseTeachers?.map((person) => ({
                    label:
                      getPersonDescription(person) +
                      ' (' +
                      person.enterprises
                        .map((enterprise) => enterprise.name)
                        .join(', ') +
                      ')',
                    value: person.user?.id,
                  }))}
                />
                <Form.Currency name="amount" label="Preis" />
                <Form.Currency
                  name="discountedAmount"
                  label="Preis (Frühbucher-Rabatt)"
                />
                {courseUnitQuery.data?.adminCourseUnit.course.earlyBirdDate && (
                  <p>
                    Für den gesamten Kurs gilt die Datumsgrenze{' '}
                    <em>
                      <Formatted.Date
                        value={
                          courseUnitQuery.data?.adminCourseUnit.course
                            .earlyBirdDate
                        }
                      />
                    </em>
                  </p>
                )}
                <Form.Date
                  name="earlyBirdDate"
                  label="Frühbucher Rabatt gültig bis (falls abweichend)"
                />
                <Form.Input
                  name="earlyBirdNotice"
                  label={
                    courseUnitQuery.data?.adminCourseUnit.course.earlyBirdNotice
                      ? `Frühbucher Hinweis (falls abweichend von "${courseUnitQuery.data.adminCourseUnit.course.earlyBirdNotice}")`
                      : 'Frühbucher Hinweis'
                  }
                />
                <Form.Checkbox name="online" label="Online-Angebot" />
                <Form.Checkbox name="correspondenceCourse" label="Fernkurs" />
                <Form.Textarea
                  name="bookingNotes"
                  label="Hinweise zur Buchung (Warenkorb und Bestellbestätigung)"
                />
                <Form.Html
                  name="description"
                  label="Beschreibung (wird z.B. im Detail-Popup/Overlay angezeigt)"
                />
              </Section>
              {courseUnitQuery.data?.adminCourseUnit.course?.category.enterprise
                .id && (
                <PropertySchemaRenderer
                  enterpriseId={
                    courseUnitQuery.data?.adminCourseUnit.course?.category
                      .enterprise.id
                  }
                  context="course-unit"
                />
              )}
            </Grid.Column>
            <Grid.Column>
              <Section title="Externe Buchung">
                <Form.Input
                  name="externalBookingLink.url"
                  label="Link zu externer Buchungsseite"
                />
                <Form.Input
                  name="externalBookingLink.label"
                  label="Link zu externer Buchungsseite (Beschriftung)"
                />
              </Section>
              <Section title="Enthaltene Module">
                <Table
                  tiny
                  columns={['Zeitraum', 'Titel', 'Tags', 'Standort', '']}
                  render={(module) => (
                    <Row
                      className={
                        module.status === CourseModuleStatus.Inactive
                          ? globalStyles.disabled
                          : undefined
                      }
                    >
                      <td>
                        <Link to={`../modules/${module.id}/edit`}>
                          {getCourseUnitDates(module)}
                        </Link>
                        {module.status === CourseModuleStatus.Inactive && (
                          <>
                            {' '}
                            <span className={globalStyles.error}>
                              [INAKTIV]
                            </span>
                          </>
                        )}
                      </td>

                      <td>{module.title}</td>
                      <td>
                        <TagInspect tags={module.tags} />
                      </td>
                      <td>
                        {module.location?.name ||
                          module.location?.city ||
                          module.location?.address?.city}
                        {module.online && <Chip>Online</Chip>}
                        {module.correspondenceCourse && <Chip>Fernkurs</Chip>}
                      </td>
                      <td className={globalStyles.flexAlignRight}>
                        <Button
                          small
                          onClick={() => onDuplicateCourseModule(module.id)}
                          label="++"
                        />
                      </td>
                    </Row>
                  )}
                  data={courseUnitQuery.data?.adminCourseUnit.modules || []}
                />
                <Button.Row>
                  <Button
                    label="Kurs-Modul erstellen"
                    onClick={onCreateCourseModule}
                    primary
                  />
                </Button.Row>
              </Section>
            </Grid.Column>
          </Grid.Row>
        </Form>
      </Frame.Content>
    </>
  );
};
